import {
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  LinearScale,
  LogarithmicScale,
  TimeScale,
  Tooltip,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
// eslint-disable-next-line import/no-extraneous-dependencies
import 'chartjs-adapter-date-fns';
import {forEach, isPlainObject} from 'lodash';
import PropTypes from 'prop-types';
import {useCallback, useEffect, useRef, useState} from 'react';

import ChartConstants, {createXAxisConfig, createYAxisConfig} from 'components/Chart/Constants';


const propTypes = {
  title: PropTypes.string,
  data: PropTypes.oneOfType([
    // simple bar chart
    PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]).isRequired,
      value: PropTypes.number.isRequired,
    })),
    // standard chart.js data
    PropTypes.shape({
      labels: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)])),
      datasets: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string.isRequired,
        data: PropTypes.arrayOf(PropTypes.number).isRequired,
        backgroundColor: PropTypes.arrayOf(PropTypes.string),
      })).isRequired,
    }),
  ]),
  showLabels: PropTypes.bool,
  /**
   * Custom chart.js `datalabels` config props
   */
  labelConfig: PropTypes.object,
  stacked: PropTypes.bool,
  horizontal: PropTypes.bool,
  xAxisLabel: PropTypes.shape({
    maxLength: PropTypes.number,
    stepSize: PropTypes.number,
    display: PropTypes.bool,
    type: PropTypes.string,
    displayTimeFormat: PropTypes.string,
    title: PropTypes.string,
  }),
  yAxisLabel: PropTypes.shape({
    truncate: PropTypes.string,
    maxLength: PropTypes.number,
    stepSize: PropTypes.number,
    display: PropTypes.bool,
    align: PropTypes.string,
    type: PropTypes.string,
    displayTimeFormat: PropTypes.string,
    title: PropTypes.string,
  }),
  clickHandler: PropTypes.func,
  className: PropTypes.string,
  style: PropTypes.object,
};

/**
 * @typedef {object} yAxisLabel
 * @property {string} [align] - default alignment is right but 'left', or 'center' are other options
 * @property {boolean} [display] - whether y-axis labels should be displayed
 * @property {string} [displayTimeFormat] - define the time format, only applicable if type is TIME_*
 * @property {number} [maxLength] - maximum length to truncate to (skip truncate if 0)
 * @property {number} [stepSize] - step size is count between ticks. If not set, tick step size is automatically determined by chart.js
 * @property {string} [title] - axis title
 * @property {string} [type] - define the scale type (use ChartConstants.scaleTypes)
 * @property {string} [truncate] - either 'middle' or 'end'
 *
 * @typedef {object} xAxisLabel
 * @property {boolean} [display] - whether x-axis labels should be displayed
 * @property {string} [displayTimeFormat] - define the time format, only applicable if type is TIME_*
 * @property {number} [maxLength] - wrap label at x-axis tick marks at specified length (no wrap if 0)
 * @property {number} [stepSize] - step size is count between ticks. If not set, tick step size is automatically determined by chart.js
 * @property {string} [title] - axis title
 * @property {string} [type] - define the scale type (use ChartConstants.scaleTypes)
 */
/**
 *
 * Renders bar chart.
 *
 * @param {object} props - props container
 * @param {string} [props.title] - assign it to dataset's label
 * @param {Array} props.data - use simple array of {label, value} or standard chart.js data structure
 * @param {boolean} props.showLabels - shows labels on bar chart
 * @param {object} props.labelConfig - new data label config to change data labels
 * @param {boolean} [props.stacked] - makes bar chart stacked if true
 * @param {boolean} [props.horizontal] - makes bar chart horizontal if true
 * @param {xAxisLabel} [props.xAxisLabel] - config object for x-axis labels
 * @param {yAxisLabel} [props.yAxisLabel] - config object for y-axis labels
 * @param {Function} [props.clickHandler] - this function is called when user clicks on a bar
 * @param {string} [props.className]
 * @param {object} [props.style]
 * @return {JSX.Element}
 */
export default function BarChart({title, data, showLabels = false,
  labelConfig = ChartConstants.defaultLabelConfig,
  stacked = false, horizontal = false,
  xAxisLabel = {
    maxLength: 10,
    stepSize: undefined,
    display: true,
    type: ChartConstants.scaleTypes.LINEAR,
    displayTimeFormat: '',
    title: '',
  },
  yAxisLabel = {
    truncate: ChartConstants.truncateOptions.MIDDLE,
    maxLength: 20,
    stepSize: undefined,
    display: true,
    align: 'right',
    type: ChartConstants.scaleTypes.LINEAR,
    displayTimeFormat: '',
    title: '',
  },
  clickHandler, className, style}) {

  const ref = useRef(null);
  // reference: https://medium.com/@teh_builder/ref-objects-inside-useeffect-hooks-eb7c15198780
  const refCallback = useCallback((node) => {
    ref.current = node;
  }, []);
  const [chart, setChart] = useState(null);

  // must set position: relative to get responsive behavior
  const finalStyle = {
    position: 'relative',
    ...style,
  };

  if (!finalStyle.height) {
    const size = isPlainObject(data) ? data.labels.length : data.length;
    finalStyle.height = `${2.5 + (size * 2.5)}rem`;
  }

  const componentIsMounted = useRef(true);
  // eslint-disable-next-line
  useEffect(() => {
    return () => {
      componentIsMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!data || data.length === 0) {
      return;
    }
    if (!ref.current) {
      return;
    }

    let labels;
    let datasets;
    if (isPlainObject(data)) {
      // noinspection JSUnresolvedVariable
      labels = data.labels;
      // noinspection JSUnresolvedVariable
      datasets = data.datasets;
      forEach(datasets, ({backgroundColor}, i) => {
        const color = [];
        for (let x = 0; x < datasets[i].data.length; x += 1) {
          color.push(backgroundColor ? backgroundColor[x % backgroundColor.length]
            : ChartConstants.colors[x % ChartConstants.colors.length]);
        }
        datasets[i].backgroundColor = color;
      });

    } else {
      labels = data.map((d) => d.label);
      const values = data.map((d) => d.value);
      const backgroundColor = data.map((d, i) => d.color || ChartConstants.colors[i % ChartConstants.colors.length]);
      datasets = [{
        data: values,
        backgroundColor,
      }];
      if (title) {
        datasets[0].label = title;
      }
    }
    const onClickProp = {};
    if (clickHandler) {
      onClickProp.onClick = (event, elements, ctx) => {
        const points = ctx.getElementsAtEventForMode(event, 'nearest', {intersect: true}, true);

        if (points.length) {
          const firstPoint = points[0];
          const label = ctx.data.labels[firstPoint.index];
          const elementValue = ctx.data.datasets[firstPoint.datasetIndex].data[firstPoint.index];
          const elementLabel = ctx.data.datasets[firstPoint.datasetIndex].label;

          clickHandler(label, elementLabel, elementValue);
        }
      };
    }

    const indexAxis = horizontal ? 'y' : 'x';
    if (!chart || indexAxis !== chart.options.indexAxis) {
      if (chart) {
        chart.destroy();
      }

      const loadChart = async () => {
        // only pull what we need for tree shaking: https://www.chartjs.org/docs/latest/getting-started/integration.html
        Chart.register(BarController, BarElement, CategoryScale, LinearScale, LogarithmicScale, TimeScale, Tooltip);
        setChart(new Chart(ref.current.getContext('2d'), {
          type: 'bar',
          data: {
            labels,
            datasets,
          },
          ...(showLabels ? {plugins: [ChartDataLabels]} : {}),
          options: {
            indexAxis: horizontal ? 'y' : 'x',
            responsive: true,
            maintainAspectRatio: false,
            ...(showLabels ? ChartConstants.defaultLayoutConfig : {}),
            plugins: {
              legend: {
                display: false,
              },
              tooltip: {
                enabled: !showLabels,
              },
              ...labelConfig,
            },
            scales: {
              x: {
                stacked,
                ...(createXAxisConfig(xAxisLabel)),
              },
              y: {
                stacked,
                ...(createYAxisConfig(yAxisLabel)),
              },
            },
            ...onClickProp,
          },
        }));
      };
      // noinspection JSIgnoredPromiseFromCall
      loadChart();
    } else {
      chart.data.labels = labels;
      chart.data.datasets = datasets;
      chart.options.onClick = onClickProp.onClick;
      chart.update();
    }

    return () => {
      if (chart && !componentIsMounted.current) {
        chart.destroy();
      }
    };
  }, [title, data, horizontal, showLabels]);

  return (
    <div className={className} style={finalStyle}>
      <canvas ref={refCallback} aria-label={title || 'Bar Chart'} role="img" />
    </div>
  );
}

BarChart.propTypes = propTypes;
